/**
 * PtyHarness — drives the `claude` CLI under a pseudo-terminal behind the
 * generic `AgentHarness` interface.  Selected at runtime via
 * `createHarness("pty")`. It is the UNCONDITIONAL harness for the task chat
 * (cards); see `resolveHarnessKind()` in `runner/query-bridge.ts`. The only way
 * to put a card on the SDK harness is the maintainer override
 * `CONVEYOR_FORCE_SDK_CARDS=1`.
 *
 * Keep-alive: the interactive `claude` process outlives any single query. When
 * a query ends via a clean transcript `result`, the harness PARKS the
 * `PtySession` (process still running) instead of killing it, so the
 * Connected-TUI stays live and interactive while the agent is idle. The next
 * query reuses that process when its resume target + spawn options match;
 * otherwise it tears the parked one down and spawns fresh. Only a real process
 * death (crash while parked, or dispose() on shutdown/hard-stop) signals
 * `pty:ended` so the server clears the ring and the tab hides.
 */

import type {
  AgentHarness,
  HarnessEvent,
  HarnessQueryOptions,
  HarnessToolDefinition,
  HarnessMcpServer,
  HarnessUserMessage,
  PtyBridge,
} from "../types.js";
import { PtySession } from "./session.js";
import { PtyMcpServer } from "./mcp-server.js";
import { ClaudeTuiAdapter } from "./adapters/claude.js";
import type { TuiAdapter } from "./adapters/types.js";
import { resolveSubmitRedeliveryMaxAttempts } from "./pty-support.js";
import { ensureUsableClaudeConfigHome, isConfigHomeFallbackActive } from "./config-home-health.js";
import { resolveTuiAuthReadiness } from "./credentials.js";
import { createServiceLogger } from "../../utils/logger.js";

export { PtySession, PtyMcpServer };

// Grace window before a process death is reported as `pty:ended`. A respawn
// (the common mid-turn abort → respawn-with-resume flow, e.g. a superseding
// chat message) within this window cancels the signal, so the Connected-TUI tab
// doesn't flap. Parked-death and dispose() bypass the grace and signal at once.
const ENDED_GRACE_MS = 4000;

export class PtyHarness implements AgentHarness {
  private static readonly log = createServiceLogger("pty-harness");

  /**
   * `bridge` relays raw terminal I/O to/from the S2 server (and on to the S5
   * terminal). It is undefined for SDK-only callers and PTY runs that never
   * attach a relay; the session simply discards stdout in that case.
   */
  constructor(
    private readonly bridge?: PtyBridge,
    private readonly adapter: TuiAdapter = new ClaudeTuiAdapter(),
  ) {}

  /** Delegated to the adapter: Claude tails a transcript + hook socket, opencode
   *  tails its plugin sink, and a raw-relay TUI has no trusted event source. */
  get emitsStructuredEvents(): boolean {
    return this.adapter.capabilities.structuredEvents;
  }

  /**
   * Does this adapter own the shared `~/.claude` config home (credentials,
   * transcripts, onboarding flags)? Claude alone does — opencode keeps its
   * state under `~/.local/share/opencode` and its event sink in the session
   * tempDir.
   *
   * Gate every `~/.claude` probe on this, NOT on
   * `capabilities.structuredEvents`. That flag meant "is this Claude" only by
   * accident — Claude was the sole adapter that had it. Once the opencode
   * adapter gained structured events via the plugin sink, the proxy silently
   * inverted and opencode cards began running Claude-only probes.
   */
  private get ownsClaudeConfigHome(): boolean {
    return this.adapter.id === "claude-code";
  }

  /** Fingerprint of the spawn-time options a reused process cannot change. */
  private fingerprintOf(options: HarnessQueryOptions): string {
    return this.adapter.spawnFingerprint({
      model: options.model,
      permissionMode: options.permissionMode,
      ...(options.appendSystemPrompt ? { appendSystemPrompt: options.appendSystemPrompt } : {}),
      cwd: options.cwd,
    });
  }

  /** The session currently streaming events, if any — repaint target. */
  private activeSession: PtySession | null = null;
  /** A completed-but-alive session kept for the next turn (keep-alive). */
  private parked: PtySession | null = null;
  /** Pending "process died" → `pty:ended` timer (grace window). */
  private endedTimer: ReturnType<typeof setTimeout> | null = null;
  /** Passive-activity subscriber, re-attached to whichever session is parked. */
  private passiveHandler: (() => void) | null = null;
  /** Once-per-session guard so the "no Claude credential" notice isn't re-posted
   *  on every respawn (a fresh spawn happens on each fingerprint/lineage flip). */
  private authNoticeSent = false;
  /** Once-per-process guard on the pod-recycle escalation (see escalateToPodRecycle). */
  private recycleRequested = false;

  /**
   * Wiggle the live pty's size so the CLI repaints its whole screen. No-op on
   * the SDK harness. Falls back to the parked session so an API reconnect while
   * the CLI is idle still re-seeds the server's scrollback ring.
   */
  forceRepaint(): void {
    (this.activeSession ?? this.parked)?.forceRepaint();
  }

  /** Actionable message posted to the card when a spawn will park at the Claude
   *  sign-in screen. Kept as a constant so the harness stays free of i18n deps. */
  private static readonly AUTH_NOT_READY_MESSAGE =
    "⚠️ This pod started the Claude Code TUI with no Claude credential, so it is " +
    "parked on the sign-in screen and can't make progress. This usually means " +
    "there is no usable Claude subscription token or API key configured for this " +
    "project/assignee. Add or repair the Claude credential in project settings, " +
    "then restart the session.";

  /**
   * Best-effort: if the TUI is about to spawn with no usable credential, post a
   * one-time diagnostic through the relay bridge. Never throws — a readiness
   * probe must not block or fail a spawn.
   */
  private async warnIfAuthNotReady(): Promise<void> {
    if (this.authNoticeSent || !this.bridge?.notifyAuthNotReady) return;
    try {
      const readiness = await resolveTuiAuthReadiness();
      if (readiness.ready) return;
      this.authNoticeSent = true;
      this.bridge.notifyAuthNotReady(PtyHarness.AUTH_NOT_READY_MESSAGE);
      PtyHarness.log.warn(
        "Claude TUI spawning with no usable credential — parked at sign-in screen",
      );
    } catch (err) {
      PtyHarness.log.warn("auth-readiness probe failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async *executeQuery(opts: {
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>;
    options: HarnessQueryOptions;
    resume?: string;
  }): AsyncGenerator<HarnessEvent, void> {
    // Self-managed-lineage adapters (opencode) report their engine-assigned
    // session id via the parked session; the executor can never resolve it
    // from disk, so a respawn (fingerprint drift, dead process) falls back to
    // that id to continue the same conversation. Claude sessions never report
    // one, so their resume semantics are untouched.
    const want = opts.resume ?? opts.options.resume ?? this.parked?.reportedSessionId ?? undefined;
    const fingerprint = this.fingerprintOf(opts.options);

    let session: PtySession;
    if (this.parked?.canReuse(want, fingerprint) && !(await this.parkedHomeDied(opts.options))) {
      // Reuse the live process: feed the follow-up prompt into the existing pty
      // instead of respawning. A new turn means no death is imminent — cancel
      // any pending ended signal.
      session = this.parked;
      this.parked = null;
      this.cancelEndedTimer();
      await session.beginTurn(opts.prompt, opts.options);
    } else {
      // Lineage/options mismatch, no parked session, or a dead one: tear down
      // any parked process (respawn — suppress its ended signal, a fresh spawn
      // follows immediately) and start a new one.
      if (this.parked) {
        const stale = this.parked;
        this.parked = null;
        this.cancelEndedTimer();
        await stale.teardown();
      }
      session = await this.spawnSession(opts.prompt, opts.options, want);
    }

    yield* this.drain(session);
    yield* this.recoverFailedDelivery(session, opts.options, want);
  }

  /**
   * Did the shared `~/.claude` mount die while this session sat parked?
   *
   * The reuse path never touched the config home, so a mount that dropped
   * during the idle window went undetected until the reused process wedged the
   * next turn exactly like the one that died. Probing here flips
   * `CLAUDE_CONFIG_DIR` (and the `~/.claude` symlink) BEFORE the decision, then
   * reports true so the caller tears the parked process down and respawns — a
   * live process cannot adopt a new config home, its env was snapshotted at
   * spawn.
   *
   * Claude-only (opencode has no `~/.claude`), and best-effort: a probe error
   * that is not a mount disconnect is a real bug the spawn path will rethrow,
   * so here it just declines to force a respawn.
   */
  private async parkedHomeDied(options: HarnessQueryOptions): Promise<boolean> {
    if (!this.ownsClaudeConfigHome) return false;
    try {
      const { fellBack } = await ensureUsableClaudeConfigHome(options.cwd, PtyHarness.log);
      if (!fellBack) return false;
      PtyHarness.log.warn(
        "shared ~/.claude mount died while the CLI was parked — respawning on the pod-local " +
          "config home instead of reusing a process bound to the dead mount",
      );
      return true;
    } catch (err) {
      PtyHarness.log.warn("parked-session config-home probe failed", {
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }

  /**
   * Spawn a fresh CLI process for `prompt`, doing the once-per-process
   * environment preparation (config-home health, credential synthesis,
   * auth-readiness warning) and registering the parked-death watch.
   */
  private async spawnSession(
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>,
    options: HarnessQueryOptions,
    want: string | undefined,
  ): Promise<PtySession> {
    const session = new PtySession(prompt, options, want, this.bridge, this.adapter);
    // Before touching the config home (credentials, transcript dir), make sure
    // the shared ~/.claude GCS FUSE mount is writable. A dropped mount surfaces
    // as ENOTCONN on the very next mkdir and wedges the session with no in-place
    // recovery; this self-heals to a pod-local config home instead of dying and
    // re-failing on every wake. Claude-only: opencode doesn't use ~/.claude.
    if (this.ownsClaudeConfigHome) {
      await ensureUsableClaudeConfigHome(options.cwd, PtyHarness.log);
    }
    // The interactive TUI ignores CLAUDE_CODE_OAUTH_TOKEN (headless-only); in
    // Conveyor cloud envs, materialize it as `<configHome>/.credentials.json`
    // so the CLI starts authenticated instead of at the login-method picker,
    // and complete the onboarding flags + workspace trust in .claude.json so
    // neither an abandoned first-run wizard nor the folder-trust dialog can
    // park the TUI. Spawn-only — a reused process is already authenticated.
    await this.adapter.prepareEnvironment({ cwd: options.cwd });
    // Credential synthesis just ran. If the CLI is still about to spawn with
    // no usable Claude credential (cloud pod, no OAuth token/API key/on-disk
    // login), the interactive TUI will park on the sign-in screen — a silent,
    // otherwise-unrecoverable freeze (raw stdout is never parsed, and the
    // event-loop heartbeat keeps the pod looking alive). Surface it once as an
    // actionable chat message so the team can fix the credential and restart.
    // Claude-only (opencode uses env-based auth); the spawn still proceeds so a
    // human at the Connected-TUI can paste a login code.
    //
    // Gated on the adapter's IDENTITY (see `ownsClaudeConfigHome`), not on
    // `capabilities.structuredEvents`. With the old gate every opencode card
    // ran this Claude-only probe, found no CLAUDE_CODE_OAUTH_TOKEN /
    // ANTHROPIC_API_KEY / ~/.claude credentials (opencode carries its own env
    // auth), and posted "started the Claude Code TUI with no Claude
    // credential" on a pod that was running opencode perfectly well.
    if (this.ownsClaudeConfigHome) {
      await this.warnIfAuthNotReady();
    }
    // Register the parked-death watch once for this process's lifetime.
    session.onExit(() => this.handleSessionExit(session));
    await session.start();
    return session;
  }

  /**
   * The turn ended because the prompt was pasted but never submitted (the
   * nudge window expired with no turn start — see `armSubmitNudge`). Enter
   * alone cannot clear every startup dialog, so the recovery is a fresh
   * process: tear down (already done by drain) and respawn with the identical
   * pasted text, bounded by `resolveSubmitRedeliveryMaxAttempts()`.
   *
   * The redelivered prompt is the session's recorded `deliveredPromptText`,
   * not `opts.prompt` — the caller's prompt is typically a single-yield async
   * generator that the first attempt already exhausted.
   *
   * When every attempt fails, yield a terminal error result. That is what turns
   * an invisible indefinite park into a visible failed turn: the runner's event
   * pipeline mirrors it to chat and reaches its normal end-of-turn bookkeeping
   * instead of waiting on a message that, for an autonomous card, never comes.
   */
  private async *recoverFailedDelivery(
    session: PtySession,
    options: HarnessQueryOptions,
    want: string | undefined,
  ): AsyncGenerator<HarnessEvent, void> {
    if (!session.promptDeliveryFailed) return;
    const text = session.deliveredPromptText;
    // Nothing to redeliver (an empty-prompt wake): respawning would just park a
    // fresh TUI with an empty input box. Report and stop.
    const maxAttempts = text ? resolveSubmitRedeliveryMaxAttempts() : 0;
    let current = session;
    for (let attempt = 1; attempt <= maxAttempts && current.promptDeliveryFailed; attempt++) {
      PtyHarness.log.warn("Prompt never submitted — respawning the TUI and redelivering", {
        attempt,
        maxAttempts,
        parkedFrame: PtyHarness.frameOf(current),
      });
      // A respawn follows immediately, so the dead process must not surface as
      // `pty:ended` and flap the Connected-TUI tab.
      this.cancelEndedTimer();
      current = await this.spawnSession(text, options, want);
      yield* this.drain(current);
    }
    if (!current.promptDeliveryFailed) return;
    const detail =
      `The agent's prompt could not be submitted to the Claude TUI after ${maxAttempts + 1} ` +
      `attempt(s) — the paste lands but the turn never starts, which usually means the CLI is ` +
      `parked on a startup dialog that Enter cannot clear. The turn is being ended so the card ` +
      `stops reporting work it is not doing.`;
    PtyHarness.log.error("Prompt delivery failed after all redelivery attempts", {
      maxAttempts,
      parkedFrame: PtyHarness.frameOf(current),
    });
    this.bridge?.notifyPromptDeliveryFailure?.(`⚠️ ${detail}`);
    this.escalateToPodRecycle();
    yield { type: "result", subtype: "error", errors: [detail] };
  }

  /**
   * Diagnostics-only view of a session's last terminal frame. Optional-safe:
   * `recoverFailedDelivery` is unit-tested with plain stand-in objects, and a
   * missing frame must never break the recovery path it is describing.
   */
  private static frameOf(session: { diagnosticFrame?: string }): string {
    return session.diagnosticFrame ?? "";
  }

  /**
   * Last resort: ask the server to recreate this pod.
   *
   * Every in-pod recovery is exhausted at this point, and the pod-local config
   * home is active — which means the shared GCS FUSE mount is confirmed dead.
   * Nothing inside the container can remount it; only a pod recreate does. The
   * server bounds this (one recycle per workspace per cooldown window), and we
   * bound it again here at once per agent process, so a park with a non-mount
   * cause can never turn into a recycle loop.
   *
   * Fire-and-forget: the escalation must not gate ending the failed turn.
   */
  private escalateToPodRecycle(): void {
    if (this.recycleRequested || !this.bridge?.requestPodRecycle) return;
    if (!isConfigHomeFallbackActive()) return;
    this.recycleRequested = true;
    PtyHarness.log.error(
      "Prompt delivery failed with the shared ~/.claude mount dead — requesting a pod recycle",
    );
    this.bridge.requestPodRecycle(
      "The shared ~/.claude mount died and the Claude CLI will not accept a prompt on this pod. " +
        "Only a new pod remounts it.",
    );
  }

  /**
   * PTY-only: subscribe to "the parked CLI produced transcript activity with no
   * query running" (a human typed into the idle Connected-TUI). Attaches to the
   * currently-parked session and to any session parked later.
   */
  /**
   * PTY-only: inject a follow-up message into the turn currently streaming
   * events (the active session), so the runner can add to a running turn without
   * aborting + respawning. Returns false when no turn is active (idle/parked) or
   * the paste couldn't be delivered — the caller supersedes instead.
   */
  injectIntoRunningTurn(text: string): boolean {
    return this.activeSession?.injectIntoRunningTurn(text) ?? false;
  }

  onPassiveActivity(handler: () => void): () => void {
    this.passiveHandler = handler;
    const unsubParked = this.parked?.onPassiveActivity(handler);
    return () => {
      if (this.passiveHandler === handler) this.passiveHandler = null;
      unsubParked?.();
    };
  }

  /**
   * PTY-only: drain a passive turn against the parked process — the human
   * already submitted in the TUI, so no prompt is fed. Empty stream when nothing
   * is parked.
   */
  async *executePassiveTurn(options: HarnessQueryOptions): AsyncGenerator<HarnessEvent, void> {
    const session = this.parked;
    if (!session) return;
    this.parked = null;
    this.cancelEndedTimer();
    session.beginPassiveTurn(options);
    yield* this.drain(session);
  }

  /**
   * Shared query/passive-turn drain: stream the session's events, then either
   * park it (clean result → keep the process for the next turn) or tear it down
   * (abort/error/early-return → real death, schedule the ended signal).
   */
  private async *drain(session: PtySession): AsyncGenerator<HarnessEvent, void> {
    this.activeSession = session;
    let clean = false;
    try {
      for await (const event of session.events()) {
        yield event;
      }
      // The stream completed. Parkable only if it ended via a clean transcript
      // result and the process is still alive.
      clean = session.endedCleanly && !session.isToreDown && !session.hasExited;
    } finally {
      if (this.activeSession === session) this.activeSession = null;
      if (clean) {
        this.park(session);
      } else {
        await session.teardown();
        // Consumer abandoned / aborted / errored → the process is (being)
        // killed. Report ended after the grace window unless a respawn beats it.
        this.scheduleEnded();
      }
    }
  }

  /** Park a live, idle session for reuse and (re)wire the passive listener. */
  private park(session: PtySession): void {
    this.parked = session;
    if (this.passiveHandler) session.onPassiveActivity(this.passiveHandler);
  }

  /**
   * Parked/active process exited on its own (crash). A mid-turn death is
   * handled by drain()'s finally; here we only act on a death while PARKED —
   * tear down the husk and report ended immediately (no respawn is coming).
   */
  private handleSessionExit(session: PtySession): void {
    if (this.parked !== session) return;
    this.parked = null;
    void session.teardown();
    this.notifyEnded();
  }

  private scheduleEnded(): void {
    if (this.endedTimer) return;
    this.endedTimer = setTimeout(() => {
      this.endedTimer = null;
      this.notifyEnded();
    }, ENDED_GRACE_MS);
  }

  private cancelEndedTimer(): void {
    if (this.endedTimer) {
      clearTimeout(this.endedTimer);
      this.endedTimer = null;
    }
  }

  private notifyEnded(): void {
    this.cancelEndedTimer();
    this.bridge?.sendEnded?.();
  }

  /**
   * Tear down every live process (parked + active) and signal ended. Called by
   * the runner on stop/shutdown so the parked CLI never outlives the session and
   * the Connected-TUI tab hides promptly.
   */
  async dispose(): Promise<void> {
    this.cancelEndedTimer();
    const toKill = [this.parked, this.activeSession].filter((s): s is PtySession => s !== null);
    this.parked = null;
    this.activeSession = null;
    for (const session of toKill) {
      try {
        await session.teardown();
      } catch {
        /* already torn down */
      }
    }
    if (toKill.length > 0) this.notifyEnded();
  }

  createMcpServer(config: { name: string; tools: HarnessToolDefinition[] }): HarnessMcpServer {
    return new PtyMcpServer(config.name, config.tools);
  }
}
